import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Clock, CheckCircle2, AlertCircle, X } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import api from "../lib/api";

export default function CandidateEmailModal({ candidate, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [activeTplId, setActiveTplId] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("compose"); // 'compose' or 'history'

  useEffect(() => {
    if (!candidate) return;
    api.get(`/admin/email-templates?candidate_id=${candidate.id}`)
      .then((r) => setTemplates(r.data))
      .catch(() => toast.error("Couldn't load templates"));
    api.get(`/admin/candidates/${candidate.id}/emails`)
      .then((r) => setHistory(r.data))
      .catch(() => {});
  }, [candidate]);

  const applyTemplate = (tpl) => {
    setActiveTplId(tpl.id);
    setSubject(tpl.subject);
    setBody(tpl.body);
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body required.");
      return;
    }
    if (!window.confirm(`Send this email to ${candidate.email}?`)) return;
    setSending(true);
    try {
      const res = await api.post(`/admin/candidates/${candidate.id}/email`, {
        subject, body, template_id: activeTplId,
      });
      if (res.data.ok) {
        toast.success(`Email sent to ${candidate.email}`);
        setSubject(""); setBody(""); setActiveTplId(null);
        const h = await api.get(`/admin/candidates/${candidate.id}/emails`);
        setHistory(h.data);
        setTab("history");
      } else {
        toast.error("Send failed — see Resend logs.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });

  if (!candidate) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="email-modal-backdrop">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="email-modal">
        {/* HEADER */}
        <div className="bg-navy text-white px-7 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[#10B981]/20 flex items-center justify-center">
              <Mail className="h-5 w-5 text-[#10B981]" />
            </div>
            <div>
              <div className="font-serif text-xl">Email Candidate</div>
              <div className="text-xs text-white/55">{candidate.full_name} · {candidate.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="email-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* TABS */}
        <div className="border-b border-navy/10 px-7 flex gap-6">
          <button
            onClick={() => setTab("compose")}
            className={`py-3 text-sm font-medium border-b-2 transition-colors ${tab === "compose" ? "text-[#10B981] border-[#10B981]" : "text-navy/55 border-transparent hover:text-navy"}`}
            data-testid="tab-compose"
          >
            Compose
          </button>
          <button
            onClick={() => setTab("history")}
            className={`py-3 text-sm font-medium border-b-2 transition-colors ${tab === "history" ? "text-[#10B981] border-[#10B981]" : "text-navy/55 border-transparent hover:text-navy"}`}
            data-testid="tab-history"
          >
            History {history.length > 0 && <span className="ml-1 text-xs">({history.length})</span>}
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-auto">
          {tab === "compose" ? (
            <div className="grid md:grid-cols-12 gap-0 h-full">
              {/* TEMPLATE LIST */}
              <div className="md:col-span-4 border-r border-navy/10 p-5 bg-ivory/50">
                <div className="eyebrow text-navy/55 mb-3">Templates</div>
                <div className="space-y-2">
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => applyTemplate(t)}
                      className={`w-full text-left p-3 rounded-md border transition-all ${activeTplId === t.id ? "border-[#10B981] bg-[#F6FDF9] ring-1 ring-[#10B981]" : "border-navy/10 hover:border-navy/25 bg-white"}`}
                      data-testid={`template-${t.id}`}
                    >
                      <div className="text-sm font-semibold text-navy">{t.label}</div>
                      <div className="text-[11px] uppercase tracking-widest text-[#D4AF37] mt-1">{t.stage}</div>
                      <div className="text-xs text-navy/55 mt-2 leading-relaxed">{t.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* COMPOSE FORM */}
              <div className="md:col-span-8 p-7 space-y-4">
                <div>
                  <Label className="text-xs text-navy/55">To</Label>
                  <div className="mt-1 px-3 py-2 rounded-md bg-navy/[0.02] border border-navy/10 text-sm text-navy">{candidate.email}</div>
                </div>
                <div>
                  <Label htmlFor="email-subj">Subject *</Label>
                  <Input id="email-subj" data-testid="email-subject" className="mt-2" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Pick a template or write your own" />
                </div>
                <div>
                  <Label htmlFor="email-body">Body *</Label>
                  <Textarea id="email-body" data-testid="email-body" rows={14} className="mt-2 font-mono text-sm leading-relaxed" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message or pick a template on the left…" />
                  <p className="text-[11px] text-navy/45 mt-1.5">Tip: pick a template on the left to auto-fill, then edit before sending. The candidate will receive this in our branded email shell.</p>
                </div>
                <div className="pt-3 flex items-center justify-between gap-3 border-t border-navy/10">
                  <div className="text-xs text-navy/55">
                    Sender: <span className="font-mono text-navy">onboarding@directhireteam.com</span> · Reply-to set to your admin email
                  </div>
                  <button onClick={send} disabled={sending || !subject.trim() || !body.trim()} className="btn-emerald !px-6 !py-2.5 disabled:opacity-50" data-testid="email-send-btn">
                    {sending ? "Sending…" : <>Send <Send className="h-4 w-4" /></>}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-7">
              {history.length === 0 ? (
                <div className="text-center py-16 text-navy/50">
                  <Mail className="h-10 w-10 mx-auto opacity-30" />
                  <div className="mt-4 text-sm">No emails sent to this candidate yet.</div>
                </div>
              ) : (
                <div className="space-y-3" data-testid="email-history-list">
                  {history.map((h) => (
                    <div key={h.id} className="card-lux !p-5" data-testid={`history-${h.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          {h.status === "sent" ? (
                            <span className="inline-flex items-center gap-1 text-[#10B981] text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Delivered to Resend</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold"><AlertCircle className="h-3.5 w-3.5" /> Failed</span>
                          )}
                          <span className="text-xs text-navy/45"><Clock className="h-3 w-3 inline" /> {fmt(h.sent_at)}</span>
                        </div>
                        {h.template_id && <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{h.template_id}</span>}
                      </div>
                      <div className="mt-3 font-semibold text-navy">{h.subject}</div>
                      <div className="mt-2 text-sm text-navy/70 leading-relaxed whitespace-pre-wrap line-clamp-4">{h.body}</div>
                      <div className="mt-3 text-[11px] text-navy/45">Sent by {h.sent_by}{h.resend_id ? ` · Resend ID ${h.resend_id.slice(0, 12)}…` : ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
